import React from 'react';
import { 
  FaCross, 
  FaChalkboardTeacher, 
  FaHandsHelping, 
  FaClinicMedical, 
  FaGraduationCap,
  FaPray,
  FaChurch,
  FaUsers,
  FaHeart,
  FaBookOpen,
  FaArrowRight
} from 'react-icons/fa';
import { motion } from 'framer-motion';

const focusAreas = [
  {
    title: "Sharing the Gospel",
    icon: <FaCross className="text-3xl" />,
    description: "Through prayer mobilization, evangelistic missions, church planting, and discipleship missions, we are reaching unreached people groups and expanding God's Kingdom.",
    verse: "Matthew 28:19",
    verseText: "\"Go therefore and make disciples of all nations, baptizing them in the name of the Father and of the Son and of the Holy Spirit.\"",
    activities: [
      "Prayer mobilization",
      "Evangelistic missions",
      "Church planting",
      "Discipleship missions"
    ],
    color: "bg-blue-100 text-blue-800 border-blue-200",
    accent: "bg-blue-600"
  },
  {
    title: "Raising and Equipping Leaders",
    icon: <FaChalkboardTeacher className="text-3xl" />,
    description: "We raise and equip servant leaders and pastors who will shepherd God's people with humility and biblical integrity.",
    verse: "2 Timothy 2:2",
    verseText: "\"And what you have heard from me in the presence of many witnesses entrust to faithful men, who will be able to teach others also.\"",
    activities: [
      "Leadership training programs",
      "Pastoral conferences",
      "Mentorship initiatives",
      "Biblical integrity workshops"
    ],
    color: "bg-indigo-100 text-indigo-800 border-indigo-200",
    accent: "bg-indigo-600"
  },
  {
    title: "Assisting the Poor",
    icon: <FaHandsHelping className="text-3xl" />,
    description: "We provide holistic and practical solutions to meet the needs of vulnerable communities.",
    verse: "Matthew 25:35-36",
    verseText: "\"For I was hungry and you gave me something to eat, I was thirsty and you gave me something to drink...\"",
    activities: [
      "Clean Water Wells (Jesus Wells)",
      "Widows and Orphans Support",
      "Micro-enterprise Development",
      "Anti-Jigger Campaigns",
      "Peacebuilding among tribes",
      "Dairy Farming Initiatives"
    ],
    color: "bg-green-100 text-green-800 border-green-200",
    accent: "bg-green-600"
  },
  {
    title: "Caring for the Sick",
    icon: <FaClinicMedical className="text-3xl" />,
    description: "We bring healing through Christ to the body, soul, and spirit.",
    verse: "Psalm 147:3",
    verseText: "\"He heals the brokenhearted and binds up their wounds.\"",
    activities: [
      "Establishing Dispensaries and Village Hospitals",
      "Counseling and Rehabilitation Centers",
      "HIV/AIDS support programs",
      "Spiritual and emotional healing ministries"
    ],
    color: "bg-red-100 text-red-800 border-red-200",
    accent: "bg-red-600"
  },
  {
    title: "Educating the Next Generation",
    icon: <FaGraduationCap className="text-3xl" />,
    description: "We invest in the future by educating and empowering children and youth.",
    verse: "Proverbs 22:6",
    verseText: "\"Train up a child in the way he should go; even when he is old, he will not depart from it.\"",
    activities: [
      "Schools (Kindergarten to High School)",
      "Vocational and Technical Training",
      "Girls' Education and Anti-FGM Advocacy",
      "Mentorship Programs",
      "Boys for Christ Sports Ministries"
    ],
    color: "bg-purple-100 text-purple-800 border-purple-200",
    accent: "bg-purple-600"
  }
];

const Focus = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-6"
        >
          <h2 className="text-4xl font-bold text-blue-900 mb-4 font-serif">Our Ministry Focus Areas</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Five pillars that guide our work and amplify our impact for Christ
          </p>
        </motion.div>

        {/* Focus Areas Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {focusAreas.map((area, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className="group"
            >
              <div className={`h-full border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 ${area.color} border-opacity-50`}>
                {/* Header with icon */}
                <div className={`p-6 ${area.accent} flex items-center justify-between`}>
                  <div className="p-3 rounded-lg bg-white bg-opacity-20">
                    {area.icon}
                  </div>
                  <div className="w-8 h-8 rounded-full bg-white bg-opacity-30 flex items-center justify-center">
                    <FaArrowRight className="text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-4">{area.title}</h3>
                  <p className="text-gray-700 mb-6">{area.description}</p>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">Key Activities:</h4>
                    <ul className="space-y-2">
                      {area.activities.map((activity, i) => (
                        <li key={i} className="flex items-start">
                          <div className={`w-2 h-2 rounded-full mt-2 mr-2 ${area.accent}`}></div>
                          <span className="text-gray-700">{activity}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="pt-4 border-t border-opacity-20 border-gray-400">
                    <blockquote className="italic text-gray-700 mb-2">"{area.verseText}"</blockquote>
                    <p className="text-sm font-medium text-gray-500">{area.verse}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Prayer CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl overflow-hidden shadow-xl"
        >
          <div className="md:flex">
            <div className="md:w-1/3 p-8 flex items-center justify-center bg-blue-800">
              <FaPray className="text-white text-6xl opacity-90" />
            </div>
            <div className="md:w-2/3 p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Join Our Prayer Network</h3>
              <p className="text-blue-100 mb-6">
                Prayer is the foundation of all we do. Partner with us in praying for:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="flex items-start">
                  <FaChurch className="text-blue-200 mt-1 mr-3 flex-shrink-0" />
                  <p className="text-blue-100">Evangelistic outreaches and church plants</p>
                </div>
                <div className="flex items-start">
                  <FaUsers className="text-blue-200 mt-1 mr-3 flex-shrink-0" />
                  <p className="text-blue-100">Leadership training programs</p>
                </div>
                <div className="flex items-start">
                  <FaHandsHelping className="text-blue-200 mt-1 mr-3 flex-shrink-0" />
                  <p className="text-blue-100">Community development projects</p>
                </div>
                <div className="flex items-start">
                  <FaHeart className="text-blue-200 mt-1 mr-3 flex-shrink-0" />
                  <p className="text-blue-100">Healing for the sick and brokenhearted</p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <button className="px-6 py-2 bg-white text-blue-800 font-medium rounded-lg hover:bg-blue-100 transition-colors">
                  Sign Up for Prayer Updates
                </button>
                <button className="px-6 py-2 border border-white text-white font-medium rounded-lg hover:bg-blue-800 transition-colors">
                  View Prayer Guide
                </button>
              </div>
              
              <div className="mt-6 pt-4 border-t border-blue-600">
                <FaBookOpen className="inline-block mr-2 text-blue-200" />
                <span className="italic text-blue-100">"The prayer of a righteous person is powerful and effective." — James 5:16</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Focus;